import { useEffect, useState } from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { useLanguage } from '@/lib/i18n';
import { useSeo } from '@/hooks/useSeo';
import { fetchPublishedJobs, Job } from '@/lib/jobs';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

const Karriere = () => {
  const { lang, tr } = useLanguage();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);

  useSeo({
    title: lang === 'de' ? 'Karriere | ViaMedica Balkan' : lang === 'bs' ? 'Karijera | ViaMedica Balkan' : 'Careers | ViaMedica Balkan',
    description:
      lang === 'de'
        ? 'Entdecken Sie aktuelle Jobausschreibungen bei ViaMedica Balkan. Wir suchen talentierte Pflegekräfte und Healthcare-Profis.'
        : lang === 'bs'
          ? 'Pronađite nove mogućnosti zapošljavanja kod ViaMedica Balkan. Tražimo talentovane medicinske radnike.'
          : 'Explore career opportunities at ViaMedica Balkan. We are hiring talented healthcare professionals.',
    canonical: `https://www.via-medica-balkan.de/karriere`,
    keywords: 'Karriere, Jobs, Pflegekräfte, Healthcare, ViaMedica Balkan, Arbeitgeber',
  });

  useEffect(() => {
    const loadJobs = async () => {
      const data = await fetchPublishedJobs(lang);
      setJobs(data);
      setLoading(false);
    };
    loadJobs();
  }, [lang]);

  const translations = {
    de: {
      title: 'Karriere',
      subtitle: 'Arbeitsplätze bei ViaMedica Balkan',
      noJobs: 'Zur Zeit keine offenen Stellen verfügbar.',
      applyButton: 'Jetzt bewerben',
      employmentType: {
        full_time: 'Vollzeit',
        part_time: 'Teilzeit',
        contract: 'Befristet',
      },
      location: 'Ort',
      salary: 'Gehalt',
      requirements: 'Anforderungen',
    },
    bs: {
      title: 'Karijera',
      subtitle: 'Poslovi kod ViaMedica Balkan',
      noJobs: 'Nema dostupnih pozicija.',
      applyButton: 'Prijavi se sada',
      employmentType: {
        full_time: 'Puno radno vrijeme',
        part_time: 'Satnično',
        contract: 'Ugovor',
      },
      location: 'Lokacija',
      salary: 'Plata',
      requirements: 'Zahtjevi',
    },
    en: {
      title: 'Careers',
      subtitle: 'Job Openings at ViaMedica Balkan',
      noJobs: 'No open positions available at the moment.',
      applyButton: 'Apply Now',
      employmentType: {
        full_time: 'Full-time',
        part_time: 'Part-time',
        contract: 'Contract',
      },
      location: 'Location',
      salary: 'Salary',
      requirements: 'Requirements',
    },
  };

  const t = translations[lang as keyof typeof translations] || translations.de;

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      <main className="flex-1 py-12 bg-gradient-to-b from-background to-muted/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold tracking-tight text-foreground sm:text-5xl mb-4">{t.title}</h1>
            <p className="text-xl text-muted-foreground">{t.subtitle}</p>
          </div>

          {/* Jobs Listing */}
          <div className="space-y-6">
            {loading ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground">Lädt...</p>
              </div>
            ) : jobs.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-muted-foreground text-lg">{t.noJobs}</p>
              </div>
            ) : (
              jobs.map((job) => (
                <Card key={job.id} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="flex justify-between items-start gap-4">
                      <div className="flex-1">
                        <CardTitle className="text-2xl mb-2">{job.title}</CardTitle>
                        <CardDescription className="text-base">
                          {job.location}
                        </CardDescription>
                      </div>
                      <Badge variant="secondary">{t.employmentType[job.employment_type]}</Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {job.salary_min && job.salary_max && (
                        <div>
                          <p className="font-semibold text-sm">{t.salary}</p>
                          <p className="text-foreground">
                            €{job.salary_min.toLocaleString()} - €{job.salary_max.toLocaleString()}
                          </p>
                        </div>
                      )}

                      <div>
                        <p className="font-semibold text-sm mb-2">Beschreibung</p>
                        <p className="text-foreground whitespace-pre-wrap">{job.description}</p>
                      </div>

                      {job.requirements && (
                        <div>
                          <p className="font-semibold text-sm mb-2">{t.requirements}</p>
                          <p className="text-foreground whitespace-pre-wrap">{job.requirements}</p>
                        </div>
                      )}

                      <div className="pt-4">
                        <Button size="lg" className="w-full">{t.applyButton}</Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Karriere;
